import React, { useMemo, useState } from 'react';
import FdModal from '../FdModal';
import FilterBar from './FilterBar';
import TablePagination, { usePagination } from '../TablePagination';
import {
  daysOverdue,
  formatCurrency,
  formatDate,
  matchesDateRange,
  normalizeSearch,
  todayIso,
  triggerCsvDownload,
  triggerExcelLikeDownload,
} from './utils';

const PAYMENT_MODES = ['Cash', 'Check', 'Bank Transfer/Deposit', 'Credit Card'];

export default function ARTab({
  sales,
  collections,
  selectedBranch,
  isLoading,
  isSubmitting,
  onPostPayment,
  onCancelAr,
  onUploadProof,
  onViewSale,
  canPostPayment,
  canCancelAr,
}) {
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [payRow, setPayRow] = useState(null);
  const [cancelRow, setCancelRow] = useState(null);
  const [cancelRemarks, setCancelRemarks] = useState('');
  const [isUploadingProof, setIsUploadingProof] = useState(false);
  const [proofError, setProofError] = useState('');
  const [payForm, setPayForm] = useState({
    ar_si_no: '',
    payment_date: todayIso(),
    mode_of_payment: '',
    paid_amount: '',
    payment_reference: '',
    proof_of_payment_name: '',
    proof_of_payment_url: '',
  });

  // Total actually collected per sale+schedule, so a partial payment keeps the
  // schedule in AR with its remaining balance instead of retiring it outright.
  const paidTotalsBySchedule = useMemo(() => {
    const totals = new Map();
    collections
      .filter((row) => String(row.status || '') === 'Paid')
      .forEach((row) => {
        const key = `${Number(row.sale_id || 0)}:${Number(row.schedule_no || 0)}`;
        totals.set(key, (totals.get(key) || 0) + Number(row.premium_amount || 0));
      });
    return totals;
  }, [collections]);

  const rows = useMemo(() => {
    const output = [];
    const roundAmount = (value) => Math.round(value * 100) / 100;

    const pushRow = (sale, scheduleNo, dueDate, amountDue, isNaf) => {
      const amountPaid = roundAmount(paidTotalsBySchedule.get(`${Number(sale.id)}:${scheduleNo}`) || 0);
      const balance = roundAmount(amountDue - amountPaid);
      if (balance <= 0.009) {
        return;
      }

      output.push({
        key: `${sale.id}:${scheduleNo}`,
        sale,
        schedule_no: scheduleNo,
        due_date: dueDate,
        amount_due: balance,
        original_amount_due: roundAmount(amountDue),
        amount_paid: amountPaid,
        is_naf: isNaf,
      });
    };

    sales.forEach((sale) => {
      if (String(sale.status || '') === 'Cancelled') {
        return;
      }
      if (selectedBranch !== 'all' && String(sale.branch || '') !== selectedBranch) {
        return;
      }

      const schedules = Array.isArray(sale.payment_schedules) ? sale.payment_schedules : [];
      if (schedules.length > 0) {
        schedules.forEach((schedule) => {
          if (Number(schedule.is_paid ? 1 : 0) === 1) {
            return;
          }

          pushRow(
            sale,
            Number(schedule.schedule_no || 0),
            schedule.due_date || sale.date || null,
            Number(schedule.amount_due || 0),
            false
          );
        });
      } else {
        pushRow(sale, 1, sale.date || null, Number(sale.premium_amount || 0), false);
      }

      const nafTotal = Number(sale.naf?.total || 0);
      if (nafTotal > 0) {
        pushRow(sale, 0, sale.date || null, nafTotal, true);
      }
    });

    return output
      .map((row) => ({
        ...row,
        days_overdue: daysOverdue(row.due_date),
      }))
      .sort((left, right) => {
        const leftDate = left.due_date || '';
        const rightDate = right.due_date || '';
        return leftDate.localeCompare(rightDate);
      });
  }, [sales, selectedBranch, paidTotalsBySchedule]);

  const filteredRows = useMemo(() => {
    const searchKey = normalizeSearch(search);
    return rows
      .filter((row) => {
        if (!searchKey) {
          return true;
        }

        const text = [
          row.sale.company_client_name,
          row.sale.transaction_no,
          row.sale.product_name,
          row.sale.agent_name,
        ]
          .map((value) => normalizeSearch(value))
          .join(' ');

        return text.includes(searchKey);
      })
      .filter((row) => matchesDateRange(row.due_date, dateFrom, dateTo));
  }, [rows, search, dateFrom, dateTo]);

  const pagination = usePagination(filteredRows);

  function openPayModal(row) {
    if (!canPostPayment) {
      return;
    }

    setPayRow(row);
    setProofError('');
    setPayForm({
      ar_si_no: '',
      payment_date: todayIso(),
      mode_of_payment: '',
      paid_amount: String(row.amount_due || ''),
      payment_reference: '',
      proof_of_payment_name: '',
      proof_of_payment_url: '',
    });
  }

  async function handleProofChange(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || typeof onUploadProof !== 'function') {
      return;
    }

    setProofError('');
    setIsUploadingProof(true);
    try {
      const result = await onUploadProof(file);
      if (result?.name && result?.url) {
        setPayForm((p) => ({ ...p, proof_of_payment_name: result.name, proof_of_payment_url: result.url }));
      } else {
        setProofError('Failed to upload proof of payment.');
      }
    } catch (err) {
      setProofError(err?.message || 'Failed to upload proof of payment.');
    } finally {
      setIsUploadingProof(false);
    }
  }

  function removeProof() {
    setPayForm((p) => ({ ...p, proof_of_payment_name: '', proof_of_payment_url: '' }));
  }

  function exportCsv() {
    triggerCsvDownload(
      `ar-${todayIso()}.csv`,
      ['Client', 'Transaction No.', 'Product', 'Schedule', 'Due Date', 'Amount Due', 'Status'],
      filteredRows.map((row) => [
        row.sale.company_client_name || '',
        row.sale.transaction_no || '',
        row.sale.product_name || '',
        row.is_naf ? 'NAF' : row.schedule_no,
        formatDate(row.due_date),
        row.amount_due,
        row.days_overdue > 0 ? `${row.days_overdue}d overdue` : 'Upcoming',
      ])
    );
  }

  function exportExcel() {
    triggerExcelLikeDownload(
      `ar-${todayIso()}.xls`,
      ['Client', 'Transaction No.', 'Product', 'Schedule', 'Due Date', 'Amount Due', 'Status'],
      filteredRows.map((row) => [
        row.sale.company_client_name || '',
        row.sale.transaction_no || '',
        row.sale.product_name || '',
        row.is_naf ? 'NAF' : row.schedule_no,
        formatDate(row.due_date),
        row.amount_due,
        row.days_overdue > 0 ? `${row.days_overdue}d overdue` : 'Upcoming',
      ])
    );
  }

  async function submitPayment() {
    if (!canPostPayment) {
      return;
    }

    const success = await onPostPayment({
      sale_id: payRow.sale.id,
      schedule_no: payRow.schedule_no,
      is_naf: payRow.is_naf,
      due_date: payRow.due_date,
      amount_due: payRow.amount_due,
      ...payForm,
    });
    if (success) {
      setPayRow(null);
    }
  }

  async function submitCancel() {
    if (!canCancelAr) {
      return;
    }

    const success = await onCancelAr({
      sale_id: cancelRow.sale.id,
      schedule_no: cancelRow.schedule_no,
      is_naf: cancelRow.is_naf,
      due_date: cancelRow.due_date,
      amount_due: cancelRow.amount_due,
      cancellation_remarks: cancelRemarks,
    });
    if (success) {
      setCancelRow(null);
      setCancelRemarks('');
    }
  }

  return (
    <div className="collections-tab-content">
      <FilterBar
        search={search}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onSearchChange={setSearch}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onExportCsv={exportCsv}
        onExportExcel={exportExcel}
      />

      {isLoading ? (
        <div className="dashboard-loading">Loading receivables...</div>
      ) : filteredRows.length === 0 ? (
        <div className="dashboard-empty">No outstanding receivables.</div>
      ) : (
        <div className="dashboard-table-wrap">
          <table className="dashboard-table collections-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Transaction No.</th>
                <th>Product</th>
                <th>Schedule</th>
                <th>Due Date</th>
                <th>Amount Due</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {pagination.pagedItems.map((row) => (
                <tr key={row.key}>
                  <td>{row.sale.company_client_name || '-'}</td>
                  <td className="collections-mono-cell">{row.sale.transaction_no || '-'}</td>
                  <td>{row.sale.product_name || '-'}</td>
                  <td>{row.is_naf ? 'NAF' : row.schedule_no}</td>
                  <td>{formatDate(row.due_date)}</td>
                  <td className="collections-number">
                    {formatCurrency(row.amount_due)}
                    {row.amount_paid > 0 ? (
                      <span className="collections-inline-note">
                        Paid {formatCurrency(row.amount_paid)} of {formatCurrency(row.original_amount_due)}
                      </span>
                    ) : null}
                  </td>
                  <td>
                    <span className={`status-badge ${row.days_overdue > 0 ? 'status-cancelled' : 'status-active'}`}>
                      {row.days_overdue > 0 ? `${row.days_overdue}d overdue` : 'Upcoming'}
                    </span>
                    {row.amount_paid > 0 ? (
                      <span className="status-badge status-pending">Partial</span>
                    ) : null}
                  </td>
                  <td>
                    <div className="dashboard-actions">
                      <button type="button" className="profile-secondary-btn profile-secondary-btn-xs" onClick={() => onViewSale(row.sale)}>
                        View
                      </button>
                      {canPostPayment ? (
                        <button type="button" className="profile-secondary-btn profile-secondary-btn-xs" onClick={() => openPayModal(row)}>
                          Pay
                        </button>
                      ) : null}
                      {canCancelAr ? (
                        <button type="button" className="profile-danger-btn profile-danger-btn-xs" onClick={() => setCancelRow(row)}>
                          Cancel
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!isLoading ? <TablePagination {...pagination} /> : null}

      <FdModal
        isOpen={Boolean(payRow)}
        onClose={() => setPayRow(null)}
        eyebrow="Collections"
        title="Apply AR Payment"
        subtitle="Post payment and create collection record."
        footer={(
          <>
            <button type="button" className="fd-btn fd-btn-cancel" onClick={() => setPayRow(null)} disabled={isSubmitting}>
              Close
            </button>
            <button
              type="button"
              className="fd-btn fd-btn-primary"
              onClick={submitPayment}
              disabled={isSubmitting || isUploadingProof || !canPostPayment || !payForm.ar_si_no || !payForm.payment_date || !payForm.mode_of_payment || !payForm.paid_amount || !payForm.proof_of_payment_url}
            >
              {isSubmitting ? 'Posting...' : 'Post Payment'}
            </button>
          </>
        )}
      >
        {payRow ? (
          <div className="collections-modal-grid">
            <div className="fd-field fd-field-wide">
              <label>Client</label>
              <input type="text" value={payRow.sale.company_client_name || ''} readOnly />
            </div>
            <div className="fd-field"><label>AR / SI No. *</label><input value={payForm.ar_si_no} onChange={(e) => setPayForm((p) => ({ ...p, ar_si_no: e.target.value }))} /></div>
            <div className="fd-field"><label>Payment Date *</label><input type="date" value={payForm.payment_date} onChange={(e) => setPayForm((p) => ({ ...p, payment_date: e.target.value }))} /></div>
            <div className="fd-field"><label>Mode of Payment *</label><select value={payForm.mode_of_payment} onChange={(e) => setPayForm((p) => ({ ...p, mode_of_payment: e.target.value }))}><option value="">Select mode</option>{PAYMENT_MODES.map((mode) => <option key={mode} value={mode}>{mode}</option>)}</select></div>
            <div className="fd-field"><label>Paid Amount *</label><input type="number" min="0" step="0.01" value={payForm.paid_amount} onChange={(e) => setPayForm((p) => ({ ...p, paid_amount: e.target.value }))} /></div>
            <div className="fd-field fd-field-wide"><label>Payment Reference</label><input value={payForm.payment_reference} onChange={(e) => setPayForm((p) => ({ ...p, payment_reference: e.target.value }))} /></div>
            <div className="fd-field fd-field-wide">
              <label>Proof of Payment *</label>
              {payForm.proof_of_payment_url ? (
                <div className="collections-attachment-row">
                  <a href={payForm.proof_of_payment_url} target="_blank" rel="noreferrer">
                    {payForm.proof_of_payment_name || 'View proof of payment'}
                  </a>
                  <button type="button" className="profile-danger-btn profile-danger-btn-xs" onClick={removeProof} disabled={isUploadingProof}>
                    Remove
                  </button>
                </div>
              ) : (
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={handleProofChange} disabled={isUploadingProof} />
              )}
              {isUploadingProof ? <span className="collections-inline-note">Uploading proof of payment...</span> : null}
              {!payForm.proof_of_payment_url && !isUploadingProof ? (
                <span className="collections-inline-note">The proof of payment file attachment is required.</span>
              ) : null}
              {proofError ? <span className="collections-danger">{proofError}</span> : null}
            </div>
          </div>
        ) : null}
      </FdModal>

      <FdModal
        isOpen={Boolean(cancelRow)}
        onClose={() => { setCancelRow(null); setCancelRemarks(''); }}
        eyebrow="Collections"
        title="Cancel AR"
        subtitle="This will remove it from active AR and log a cancelled record."
        footer={(
          <>
            <button type="button" className="fd-btn fd-btn-cancel" onClick={() => { setCancelRow(null); setCancelRemarks(''); }} disabled={isSubmitting}>
              Close
            </button>
            <button type="button" className="fd-btn fd-btn-primary" onClick={submitCancel} disabled={isSubmitting || !canCancelAr || !cancelRemarks.trim()}>
              {isSubmitting ? 'Saving...' : 'Confirm Cancel'}
            </button>
          </>
        )}
      >
        {cancelRow ? (
          <div className="collections-modal-grid">
            <div className="fd-field fd-field-wide"><label>Client</label><input value={cancelRow.sale.company_client_name || ''} readOnly /></div>
            <div className="fd-field"><label>Schedule</label><input value={cancelRow.is_naf ? 'NAF' : cancelRow.schedule_no} readOnly /></div>
            <div className="fd-field"><label>Amount</label><input value={formatCurrency(cancelRow.amount_due)} readOnly /></div>
            <div className="fd-field fd-field-wide">
              <label>Cancellation Remarks *</label>
              <textarea className="collections-textarea" value={cancelRemarks} onChange={(event) => setCancelRemarks(event.target.value)} />
            </div>
          </div>
        ) : null}
      </FdModal>
    </div>
  );
}
